import { useState } from 'react';
import logo from '../../assets/images/logo.png';
import AssetFields from './fields/asset-fields';
import TimeframeFields from './fields/timeframe-fields';
import ArtStyleFields from './fields/art-style-fields';

const getCurrentDateValue = () => {
    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - timezoneOffset).toISOString().split('T')[0];
};

const Form = ({ onSubmit = () => {}, isSubmitting = false, apiError = null }) => {
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [timeframe, setTimeframe] = useState('');
    const [dateRange, setDateRange] = useState({
        start: '',
        end: '',
    });
    const [selectedArtStyle, setSelectedArtStyle] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const clearValidationError = (field) => {
        setValidationErrors((currentErrors) => {
            if (!currentErrors[field]) {
                return currentErrors;
            }

            const nextErrors = { ...currentErrors };
            delete nextErrors[field];
            return nextErrors;
        });
    };

    const handleAssetChange = (asset) => {
        setSelectedAsset(asset);

        if (asset) {
            clearValidationError('asset');
        }
    };

    const handleTimeframeChange = (nextTimeframe) => {
        setTimeframe(nextTimeframe);

        if (nextTimeframe) {
            clearValidationError('timeframe');
        }
    };

    const handleDateRangeChange = (nextDateRange) => {
        setDateRange(nextDateRange);

        const today = getCurrentDateValue();
        const effectiveEnd = nextDateRange.end || today;

        if (nextDateRange.start && nextDateRange.start <= effectiveEnd) {
            clearValidationError('dateRange');
        }
    };

    const handleArtStyleChange = (artStyle) => {
        setSelectedArtStyle(artStyle);

        if (artStyle) {
            clearValidationError('artStyle');
        }
    };

    const validateForm = (effectiveEnd) => {
        const nextErrors = {};

        if (!selectedAsset) {
            nextErrors.asset = 'Select an asset.';
        }

        if (!timeframe) {
            nextErrors.timeframe = 'Select a timeframe.';
        }

        if (!dateRange.start) {
            nextErrors.dateRange = 'Choose a start date.';
        } else if (dateRange.start > effectiveEnd) {
            nextErrors.dateRange =
                'The start date must be on or before the end date.';
        }

        if (!selectedArtStyle) {
            nextErrors.artStyle = 'Select an art style.';
        }

        return nextErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const today = getCurrentDateValue();
        const effectiveEnd = dateRange.end || today;

        if (!dateRange.end) {
            setDateRange((current) => ({ ...current, end: today }));
        }

        const nextErrors = validateForm(effectiveEnd);
        setValidationErrors(nextErrors);

        if (Object.keys(nextErrors).length === 0) {
            onSubmit({
                asset: selectedAsset,
                timeframe,
                dateRange: { start: dateRange.start, end: effectiveEnd },
                artStyle: selectedArtStyle,
            });
        }
    };

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    return (
        <section id="form-section">
            <div id="form-container">
                <form onSubmit={handleSubmit} noValidate>
                    <div id="form-header">
                        <img src={logo} alt="chArtIt Logo" />
                        <h2 className="sr-only">chArtIt</h2>
                        <p>
                            Pick an asset. Choose a timeframe. Choose an art
                            style.
                        </p>
                    </div>
                    <div id="form-columns">
                        <AssetFields
                            value={selectedAsset}
                            onChange={handleAssetChange}
                            error={validationErrors.asset}
                        />
                        <TimeframeFields
                            timeframe={timeframe}
                            dateRange={dateRange}
                            onTimeframeChange={handleTimeframeChange}
                            onDateRangeChange={handleDateRangeChange}
                            errors={{
                                timeframe: validationErrors.timeframe,
                                dateRange: validationErrors.dateRange,
                            }}
                        />
                        <ArtStyleFields
                            value={selectedArtStyle}
                            onChange={handleArtStyleChange}
                            error={validationErrors.artStyle}
                        />
                    </div>
                    <div id="form-footer">
                        {hasValidationErrors && (
                            <p
                                className="form-validation-summary"
                                role="alert"
                            >
                                Please complete the required fields.
                            </p>
                        )}
                        {apiError && !hasValidationErrors && (
                            <p
                                className="form-validation-summary"
                                role="alert"
                            >
                                {apiError}
                            </p>
                        )}
                        <button
                            className="button"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Generating...' : 'Generate Art'}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
};

export default Form;
